import { useEffect, useState, useMemo, type CSSProperties } from "react";
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Divider,
  Popconfirm,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import {
  usersApi,
  type Trabajador,
  type TrabajadorCreate,
} from "../../services/users.api";
import { configOptionApi } from "../../services/configOption.api";
import { formatRut } from "../../utils/formatRut";
import { formatPhoneConCodigo } from "../../utils/formatPhone";
import TelefonoInput from "../../Components/TelefonoInput";
import { reglaTelefono, telefonoParaEnviar } from "../../utils/telefonoE164";
import { marcarCampoTelefono } from "../../utils/telefonoErrorApi";
import { irAlPrimerError } from "../../utils/formularioErrores";
import { clavesCambiadas } from "../../utils/camposCambiados";
import type { ErrorApi } from "../../services/apiClient";
import { norm } from "../../utils/fuzzyMatch";
// Bridge antd estático: los métodos `Modal.x` / `message.x` /
// `notification.x` importados directo de "antd" no funcionan en
// v5 + React 19 (root React huérfano fuera del ConfigProvider).
// Round 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg } from "../../utils/antdStatic";

import { paginacionCliente } from "../../utils/tablaPaginacion";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import { useEsMovil } from "../aseos/components/useMediaQuery";
import "../../Css/tabla-fichas-movil.css";
/**
 * Página /trabajadores — gestión de empleados de Aloja Fácil que NO son
 * aseadores ni propietarios y que NO tienen login.
 *
 * Round 2026-04-30 — creada para resolver el problema reportado: los
 * pagos esporádicos a personas externas (mantención, supervisor,
 * contador, etc.) terminaban como `rol="otro"` que el backend
 * normalizaba a "cliente", rompiendo el filtro "Pagado a (otro)" en
 * /finanzas-empresa.
 *
 * Decisión de scope:
 *  - Sin login (no se setea password). El endpoint `/api/auth/login`
 *    rechaza con 403 a cualquier rol fuera de la whitelist.
 *  - Sin propiedades asociadas — un trabajador puede recibir pagos por
 *    múltiples propiedades, no es 1:1.
 *  - CRUD completo desde acá. Para pagos asociados, usar /sueldos o
 *    /gastos seleccionando rol "Trabajador" en el modal.
 */
/**
 * Medidas responsivas (ronda F3, H10). A 360 px la página era más ancha que la pantalla y el modal cortaba el campo de teléfono (el
 * selector de país + el número) y su mensaje de error: el buscador medía 360 px fijos, el modal 720 px y el formulario tenía SIEMPRE
 * dos columnas. Ahora todo se achica al ancho disponible. Se exportan para que la prueba de estilo las mida.
 */
export const ANCHO_MODAL = 720;
/** Nunca más ancho que la pantalla menos 16 px a cada lado, aunque el ancho pedido sea mayor. */
export const ESTILO_MODAL: CSSProperties = { maxWidth: "calc(100vw - 32px)" };
/** Dos columnas cuando caben (columnas de al menos 240 px); una sola en un celular. */
export const ESTILO_FORMULARIO: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
  gap: "0 16px",
};
/** Barra del botón y el buscador: se reparte en filas en vez de desbordar. */
export const ESTILO_BARRA_BUSQUEDA: CSSProperties = { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16, width: "100%" };
/** El buscador crece hasta 360 px, parte de 240 px y se puede achicar más (`minWidth: 0`). */
export const ESTILO_BUSCADOR: CSSProperties = { flex: "1 1 240px", maxWidth: 360, minWidth: 0 };

export default function TrabajadoresPage() {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const [data, setData] = useState<Trabajador[]>([]);
  const [loading, setLoading] = useState(false);
  // La búsqueda se recuerda por usuario admin en este navegador (el <Input> de
  // abajo es controlado, así que al volver el texto restaurado se ve).
  const [search, setSearch] = useFiltroPersistido("trabajadores.busqueda", "", {
    validar: (v) => typeof v === "string",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Trabajador | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<TrabajadorCreate>();
  // Celular: la tabla de ~800 px deja Editar y Eliminar fuera de la pantalla (medido a 360 px: a 674 px del borde izquierdo y sin que
  // la columna fija se ancle); ahí cada fila pasa a una ficha de una columna con los botones a la vista (ronda F3b).
  const esMovil = useEsMovil();

  // Catálogos compartidos del sistema (Configuración → Opciones generales).
  // El usuario reportó que el modal "Nuevo trabajador" usaba inputs libres
  // para Banco y Tipo de cuenta cuando ya existen valores predeterminados
  // gestionables en /configuracion. Round 2026-04-30 decies — los traemos
  // del mismo endpoint que usa /clientes para que admin no tenga que
  // tipear "BancoEstado" cinco veces.
  const [availableBanks, setAvailableBanks] = useState<string[]>([]);
  const [accountTypes, setAccountTypes] = useState<string[]>([]);

  const fetch = async () => {
    setLoading(true);
    try {
      const list = await usersApi.getTrabajadores();
      setData(list ?? []);
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogos = async () => {
    const [bancos, cuentas] = await Promise.all([
      configOptionApi.list("banco"),
      configOptionApi.list("tipocuenta"),
    ]);
    setAvailableBanks(bancos?.data?.map((o) => o.valor) ?? []);
    setAccountTypes(cuentas?.data?.map((o) => o.valor) ?? []);
  };

  useEffect(() => {
    void fetch();
    void fetchCatalogos();
  }, []);

  const filtered = useMemo(() => {
    const q = norm(search);
    if (!q) return data;
    // Solo dígitos para teléfono (la DB lo guarda "pegado" pero la tabla lo
    // muestra agrupado) y RUT (se guarda con puntos/guión pero se suele
    // tipear sin ellos) — auditoría 2026-07-13. `norm` además pliega tildes
    // ("perez" encuentra "Pérez"), como el resto del ERP.
    const qDigits = search.replace(/\D/g, "");
    return data.filter((t) => {
      const hay = norm(
        [t.nombre, t.email, t.telefono, t.rut, t.descripcion]
          .filter(Boolean)
          .join(" "),
      );
      if (hay.includes(q)) return true;
      if (!qDigits) return false;
      const digitsHay = [t.telefono, t.rut]
        .filter(Boolean)
        .map((v) => String(v).replace(/\D/g, ""))
        .join(" ");
      return digitsHay.includes(qDigits);
    });
  }, [data, search]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (t: Trabajador) => {
    setEditing(t);
    form.setFieldsValue({
      nombre: t.nombre,
      email: t.email,
      telefono: t.telefono,
      rut: t.rut,
      banco: t.banco,
      tipocuenta: t.tipocuenta,
      numerocuenta: t.numerocuenta,
      descripcion: t.descripcion,
    });
    setModalOpen(true);
  };

  const submit = async () => {
    if (submitting) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload: TrabajadorCreate = {
        nombre: values.nombre.trim(),
        email: (values.email ?? "").trim(),
        // El teléfono ya viene validado con su código de país (o vacío): `null` EXPLÍCITO para vaciar, el canónico si hay número y
        // OMITIDO (undefined) si no cambió respecto del trabajador que se edita; así tampoco se reenvía un dato antiguo sin código.
        telefono: telefonoParaEnviar(values.telefono, editing ? (editing.telefono ?? null) : undefined),
        rut: (values.rut ?? "").trim(),
        banco: (values.banco ?? "").trim(),
        tipocuenta: (values.tipocuenta ?? "").trim(),
        numerocuenta: (values.numerocuenta ?? "").trim(),
        descripcion: (values.descripcion ?? "").trim(),
      };
      // Un rechazo del servidor por el teléfono (formato) se marca además junto al campo.
      const alFallar = (error: ErrorApi) => {
        marcarCampoTelefono(form, "telefono", error);
      };
      // `sinAvisoDeExito` al EDITAR: el aviso de éxito lo decide esta pantalla DESPUÉS de mirar la respuesta. Si lo diera apiFetch,
      // un servidor que ignora el `null` (lo trata como «no tocar») mostraba «Trabajador actualizado» y, encima, la advertencia de que
      // el teléfono no se quitó: dos mensajes que se contradicen (mismo arreglo que en Mi perfil, ronda F2 G5).
      const result = editing
        ? await usersApi.updateTrabajador(editing.id, payload, { alFallar, sinAvisoDeExito: true })
        : await usersApi.createTrabajador(payload, { alFallar });
      if (!result) return;
      if (editing) {
        // Pidió vaciar el teléfono y el servidor lo conservó: no se calla ni se anuncia un éxito que no fue.
        const noSeQuitoElTelefono = payload.telefono === null && !!result.telefono;
        const hayOtrosCambios =
          clavesCambiadas(editing as unknown as Record<string, unknown>, payload as unknown as Record<string, unknown>, [
            "nombre",
            "email",
            "rut",
            "banco",
            "tipocuenta",
            "numerocuenta",
            "descripcion",
          ]).length > 0;
        if (noSeQuitoElTelefono && !hayOtrosCambios) {
          // Lo ÚNICO que se pidió no se aplicó: UN solo mensaje y el modal queda abierto (no se cierra como si hubiera funcionado; la
          // lista seguiría mostrando el número). Antes el campo se quedaba VACÍO y sin decir qué hacer: parecía que el número ya no
          // estaba y guardar de nuevo repetía lo mismo (ronda F3b). Ahora el campo vuelve a mostrar el número que SÍ está guardado y
          // lo dice, también bajo el campo (el aviso de arriba dura unos segundos). Editarlo borra la nota.
          const aviso = `No se pudo quitar el teléfono: sigue guardado el anterior (${formatPhoneConCodigo(result.telefono)}). Lo volvimos a poner en el campo; para cambiarlo, escribe otro número.`;
          form.setFields([{ name: "telefono", value: result.telefono, errors: [], warnings: [aviso] }]);
          getMsg()?.warning(aviso);
          return;
        }
        if (noSeQuitoElTelefono) {
          // Lo demás sí se guardó: se cierra y se avisa UNA vez por qué el teléfono sigue ahí.
          getMsg()?.warning("Se guardaron los demás cambios, pero no se pudo quitar el teléfono: sigue guardado el anterior.");
        } else {
          getMsg()?.success("Trabajador actualizado");
        }
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      await fetch();
    } catch (err: any) {
      if (err?.errorFields) {
        // Error de validación: ya quedó marcado en el campo, falta llevar a la persona hasta el primero (en el celular el modal es
        // más alto que la pantalla y, sin esto, «Guardar» no hacía NADA visible).
        irAlPrimerError(form, err);
      } else {
        getMsg()?.error("Error al guardar trabajador");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id: number) => {
    const ok = await usersApi.deleteUser(id);
    if (ok) await fetch();
  };

  // Los botones de una fila: los comparten la tabla de escritorio y la ficha del celular.
  const accionesDe = (t: Trabajador) => (
    <Space wrap>
      <Button icon={<EditOutlined />} onClick={() => openEdit(t)}>
        Editar
      </Button>
      <Popconfirm
        title="Eliminar trabajador"
        description="¿Estás seguro? Los pagos asociados se conservarán pero quedarán huérfanos."
        okText="Eliminar"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
        onConfirm={() => onDelete(t.id)}
      >
        <Button danger icon={<DeleteOutlined />}>
          Eliminar
        </Button>
      </Popconfirm>
    </Space>
  );

  // Celular (ronda F3b): UNA columna por fila, la ficha. Solo muestra los datos que la persona tiene cargados.
  const columnsMovil: ColumnsType<Trabajador> = [
    {
      key: "ficha",
      render: (_, t) => {
        const cuenta = [t.banco, t.tipocuenta, t.numerocuenta].filter(Boolean).join(" · ");
        const dato = (etiqueta: string, valor: string | undefined | null) =>
          valor ? (
            <div className="ficha-dato">
              <span className="ficha-dato-etiqueta">{etiqueta}</span>
              <span>{valor}</span>
            </div>
          ) : null;
        return (
          <div className="ficha">
            <div className="ficha-titulo">
              <span>{t.nombre}</span>
            </div>
            {dato("RUT", t.rut)}
            {dato("Email", t.email)}
            {dato("Teléfono", t.telefono ? formatPhoneConCodigo(t.telefono) : "")}
            {dato("Cuenta", cuenta)}
            {dato("Descripción", t.descripcion)}
            <div className="ficha-acciones">{accionesDe(t)}</div>
          </div>
        );
      },
    },
  ];

  const columns: ColumnsType<Trabajador> = [
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      sorter: (a, b) => (a.nombre ?? "").localeCompare(b.nombre ?? ""),
      render: (v: string) => <b>{v}</b>,
    },
    {
      title: "RUT",
      dataIndex: "rut",
      key: "rut",
      width: 140,
      render: (v: string) => v || "—",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (v: string) => v || "—",
    },
    {
      title: "Teléfono",
      dataIndex: "telefono",
      key: "telefono",
      width: 140,
      // Con el código de país separado (+56 9 1234 5678), igual que Administradores y /aseadores: el mismo número se ve igual en
      // todas las pantallas. Un dato antiguo sin código se muestra como siempre.
      render: (v: string) => formatPhoneConCodigo(v) || "—",
    },
    {
      title: "Banco",
      dataIndex: "banco",
      key: "banco",
      width: 140,
      render: (v: string) => (v ? <Tag>{v}</Tag> : "—"),
    },
    {
      title: "N° Cuenta",
      dataIndex: "numerocuenta",
      key: "numerocuenta",
      width: 160,
      render: (v: string) => v || "—",
    },
    {
      title: "Descripción",
      dataIndex: "descripcion",
      key: "descripcion",
      ellipsis: true,
      render: (v: string) => v || "—",
    },
    {
      title: "Acciones",
      key: "acciones",
      fixed: "right",
      width: 180,
      render: (_, t) => accionesDe(t),
    },
  ];

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <div className="finanzas-page">
        <PageHeader
          title="Trabajadores"
          helpText="Empleados de Aloja Fácil que no son aseadores, clientes ni propietarios. No tienen login. Útil para registrar a quién se le ha pagado o se le debe (mantención, supervisor, contador externo, proveedores frecuentes)."
        />

        {/* El buscador crece hasta 360 px pero NUNCA más que el ancho disponible (antes `width: 360` fijo hacía la página más ancha que
            un celular de 360 px y empujaba el borde derecho de todo, el modal incluido): `flex` con base de 240 px, tope y sin mínimo. */}
        <div style={ESTILO_BARRA_BUSQUEDA}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuevo trabajador
          </Button>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Buscar por nombre, RUT, email, teléfono…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={ESTILO_BUSCADOR}
          />
        </div>

        <Table<Trabajador>
          className={esMovil ? "tabla-fichas" : undefined}
          rowKey="id"
          loading={loading}
          dataSource={filtered}
          columns={esMovil ? columnsMovil : columns}
          showHeader={!esMovil}
          pagination={paginacionCliente({ porDefecto: 20, etiqueta: "trabajador" })}
          scroll={esMovil ? undefined : { x: 1100 }}
        />

        <Modal
          title={editing ? "Editar trabajador" : "Nuevo trabajador"}
          open={modalOpen}
          onCancel={() => {
            if (submitting) return;
            setModalOpen(false);
            setEditing(null);
          }}
          onOk={submit}
          okText={editing ? "Guardar" : "Crear"}
          confirmLoading={submitting}
          okButtonProps={{ disabled: submitting }}
          maskClosable={!submitting}
          width={ANCHO_MODAL}
          style={ESTILO_MODAL}
          destroyOnClose
        >
          <Form form={form} layout="vertical" style={ESTILO_FORMULARIO}>
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[
                { required: true, message: "Ingresa un nombre" },
                { min: 2, message: "Mínimo 2 caracteres" },
              ]}
              style={{ gridColumn: "1 / -1" }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="rut"
              label="RUT"
              // Patrón canónico AntD para transformar el valor en vivo:
              // `getValueFromEvent` intercepta el evento del Input ANTES
              // de que el Form guarde el value en su state interno, así
              // el value formateado (12.345.678-9) es el que termina
              // mostrándose. El alternativo `onChange + setFieldsValue`
              // que usa /clientes tiene un edge case en AntD v5 donde el
              // onChange interno del Form pisa el setFieldsValue y el
              // input nunca se actualiza visualmente (reproducido al
              // pegar "68768678" y verlo sin puntos).
              getValueFromEvent={(e) => formatRut(e.target.value)}
            >
              <Input placeholder="12.345.678-9" maxLength={12} />
            </Form.Item>
            {/* Teléfono con código de país. El trabajador no tiene login ni usa el bot: el valor puede quedar vacío, pero si se escribe
                tiene que estar completo (así se guarda "todo pegado" y se muestra agrupado). */}
            <Form.Item name="telefono" label="Teléfono" rules={[reglaTelefono({ valorInicial: editing?.telefono })]}>
              <TelefonoInput ariaLabel="Teléfono del trabajador" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: "email", message: "Email inválido" }]}
              style={{ gridColumn: "1 / -1" }}
            >
              <Input placeholder="trabajador@ejemplo.com" />
            </Form.Item>
            <Form.Item name="banco" label="Banco">
              <Select
                showSearch
                allowClear
                placeholder="Selecciona banco"
                options={availableBanks.map((b) => ({ label: b, value: b }))}
                optionFilterProp="label"
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: "8px 0" }} />
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        padding: "0 8px 8px",
                      }}
                    >
                      <Input
                        placeholder="Nuevo banco..."
                        id="new-banco-trabajadores"
                        onPressEnter={async (e) => {
                          const input = e.currentTarget;
                          const val = input.value.trim();
                          if (!val) return;
                          await configOptionApi.create({
                            categoria: "banco",
                            valor: val,
                          });
                          setAvailableBanks((prev) => [...prev, val]);
                          form.setFieldValue("banco", val);
                          input.value = "";
                        }}
                      />
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={async () => {
                          const input = document.getElementById(
                            "new-banco-trabajadores",
                          ) as HTMLInputElement;
                          const val = input?.value.trim();
                          if (!val) return;
                          await configOptionApi.create({
                            categoria: "banco",
                            valor: val,
                          });
                          setAvailableBanks((prev) => [...prev, val]);
                          form.setFieldValue("banco", val);
                          if (input) input.value = "";
                        }}
                      >
                        Crear
                      </Button>
                    </div>
                  </>
                )}
              />
            </Form.Item>
            <Form.Item name="tipocuenta" label="Tipo de cuenta">
              <Select
                showSearch
                allowClear
                placeholder="Selecciona tipo de cuenta"
                options={accountTypes.map((t) => ({ label: t, value: t }))}
                optionFilterProp="label"
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: "8px 0" }} />
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        padding: "0 8px 8px",
                      }}
                    >
                      <Input
                        placeholder="Nuevo tipo..."
                        id="new-tipocuenta-trabajadores"
                        onPressEnter={async (e) => {
                          const input = e.currentTarget;
                          const val = input.value.trim();
                          if (!val) return;
                          await configOptionApi.create({
                            categoria: "tipocuenta",
                            valor: val,
                          });
                          setAccountTypes((prev) => [...prev, val]);
                          form.setFieldValue("tipocuenta", val);
                          input.value = "";
                        }}
                      />
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={async () => {
                          const input = document.getElementById(
                            "new-tipocuenta-trabajadores",
                          ) as HTMLInputElement;
                          const val = input?.value.trim();
                          if (!val) return;
                          await configOptionApi.create({
                            categoria: "tipocuenta",
                            valor: val,
                          });
                          setAccountTypes((prev) => [...prev, val]);
                          form.setFieldValue("tipocuenta", val);
                          if (input) input.value = "";
                        }}
                      >
                        Crear
                      </Button>
                    </div>
                  </>
                )}
              />
            </Form.Item>
            <Form.Item
              name="numerocuenta"
              label="N° de cuenta"
              style={{ gridColumn: "1 / -1" }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="descripcion"
              label="Descripción"
              tooltip="Notas internas: rol que cumple, frecuencia de pago, contacto adicional, etc."
              style={{ gridColumn: "1 / -1" }}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Layout>
  );
}
